import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Modal,
  Popover,
  OverlayTrigger,
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Formik } from "formik";
import { jwtDecode } from "jwt-decode";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import FocusError from "../../../components/error/error-focus";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import InputField from "../../../components/input/input-field";
import "react-phone-input-2/lib/style.css";
import Select from "react-select";
import FadeLoaderComponent from "../../../components/loader-component";
import { useMemo } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { roleTypes } from "../../../data/roleType";
import DatePickerField from "../../../components/input/date-picker-field";
import * as SettingSelector from "../../../store/setting/selectors";
import { useSelector } from "react-redux";

const OrderRequestView = () => {
  const alertRef = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const alertRefProduct = useRef(null);
  const [loader, setLoader] = useState(true);
  const [errors, setErrors] = useState({});
  const [decodedToken, setDecodedToken] = useState();

  const [warehouses, setWarehouses] = useState([]);
  const token = localStorage.getItem("coyote-token");

  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  const [selectedData, setSelectedData] = useState();
  const permissionsWarehouse = useSelector(SettingSelector?.set_permissions);
  const warehouseHasCreatePermission =
    permissionsWarehouse?.warehouse?.create === true;
  const handleShowView = async (item) => {
    if (item) {
      setSelectedData({
        id: item.id || null,
        shipTo: item.ship_to || null,
        poNumber: item.po_number || null,
        warehouseId: item?.warehouse_id,
        warehouseName: item?.warehouse_name,
        vendorName: item?.vendor_name,
        date: item.date || "",
        orderItems: item.order_items?.map((data) => ({
          id: data.product_id,
          name: data.name,
          description: data.description,
          amount: data.amount,
          sku: data.sku,
          inventory_type: data.inventory_type,
          quantity: data.quantity,
          manufacturer_name: data.manufacturer_name,
          rate: data.rate,
          job_name: data.job_name,
          site_name: data.site_name,
          system_name: data.system_name,
          phase_name: data.phase_name,
          received_quantity: data.received_quantity,
          status: data.status || null,
          model: data.model,
          warehouse: data.warehouse_name,
        })),
      });
      setLoader(false);
    }
  };

  const getOrder = async () => {
    const config = {
      headers: {
        Authorization: `${token}`,
      },
    };

    try {
      const response = await axios.get(
        `${url.backendURL}order/${id}/detail`,
        config
      );
      const productRequests = response.data.response.data;
      handleShowView(productRequests);
    } catch (err) {
      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
      handleError(err, "information");
      setLoader(false);
    }
  };

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const getWarehouses = async () => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };
      const response = await axios.get(
        `${url.backendURL}warehouse/all/lists`,
        config
      );
      setWarehouses(response.data.response.data);
    } catch (err) {
      handleError(err, "user-edit");
    }
  };

  useEffect(() => {
    if (token && decodedToken) {
      const fetchData = async () => {
        await getWarehouses();

        await getOrder();
        setLoader(false);
      };
      fetchData();
    }
  }, [token, decodedToken]);

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response.data.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${decodedToken?.roles?.some(
          (role) => role.role_type === roleTypes.SUPER_ADMIN
        )
          ? "admin"
          : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const [warehouseOptions, setWarehouseOptions] = useState([]);

  useEffect(() => {
    const warehouseOptions =
      warehouses.length > 0 &&
      warehouses.map((warehouse) => ({
        value: warehouse.id,
        label: warehouse.name,
        address: warehouse.address,
      }));
    setWarehouseOptions(warehouseOptions || []);
  }, [warehouses]);

  const productColumns = useMemo(
    () => [
      { 
        accessorFn: (row) => row.description || "", 
        id: "description", 
        header: "Description", 
        size: 150 
      },
      { accessorKey: "model", header: "Model", size: 150 },
      { accessorKey: "warehouse", header: "Warehouse", size: 150 },
      { accessorKey: "quantity", header: "Quantity", size: 150 },
      {
        accessorKey: "received_quantity",
        header: "Received Quantity",
        size: 150,
      },
      { accessorKey: "status", header: "Status", size: 150 },
      { accessorKey: "job_name", header: "Job", size: 150 },
      { accessorKey: "rate", header: "Rate", size: 150 },
    ],
    []
  );

  const [draggingRowProduct, setDraggingRowProduct] = useState(null);
  const [hoveredTableProduct, setHoveredTableProduct] = useState(null);

  const commonTableProps = {
    selection: false,
    autoResetPageIndex: false,
    enableRowDragging: false,
    enableFullScreenToggle: false,
    muiSelectCheckboxProps: {
      color: "secondary",
    },

    muiTableContainerProps: {
      sx: {
        minHeight: "320px",
      },
    },
    onDraggingRowChange: setDraggingRowProduct,
    state: { draggingRowProduct },
  };

  const tableProduct = useMaterialReactTable({
    ...commonTableProps,
    columns: productColumns,
    enableColumnFilters: false,
    data: selectedData?.orderItems ? selectedData?.orderItems : [],

    defaultColumn: {
      size: 100,
    },
    getRowId: (originalRow) => `product-${originalRow.id}`,

    muiRowDragHandleProps: {
      onDragEnd: () => { },
    },
    muiTablePaperProps: {
      onDragEnter: () => setHoveredTableProduct("product"),
      sx: {
        outline:
          hoveredTableProduct === "product" ? "2px dashed #216CDC" : undefined,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor:
          row.original.status === "in-active"
            ? "#bcb8b8"
            : row.original.moved
              ? "#216CDC"
              : "inherit",
        pointerEvents: row.original.status === "in-active" ? "none" : "auto",
        cursor: row.original.status === "in-active" ? "no-drop" : "pointer",
      },
    }),
  });

  return (
    <>
      <div>
        <Formik
          enableReinitialize={true}
          initialValues={{
            ...selectedData,
          }}
        >
          {({ values }) => (
            <Form>
              <FocusError />
              <Row>
                <Col lg="12" ref={alertRef}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between">
                      <div
                        className="breadcrumb header-title"
                        style={{ cursor: "pointer" }}
                      >
                        <h4
                          onClick={() =>
                            navigate("/dashboard/app/purchase-order-list")
                          }
                          className="text-primary card-title"
                          style={{ cursor: "pointer" }}
                        >
                          <span>Purchase Order</span>
                        </h4>
                        <h4 className="mx-2 card-title">{">"}</h4>
                        <h4 className="card-title">Details</h4>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <AlertNotificationSimple
                        type={alert.type}
                        message={alert.message}
                        show={alert.show}
                        onClose={() => setAlert({ ...alert, show: false })}
                      />
                      {selectedData && !loader ? (
                        <>
                          <Row className="mb-4 new-order-info">
                            <Col md={8} className="d-flex align-items-end">
                              <Row md={12} style={{ width: "100%" }}>
                                <Col md={4}>
                                  <DatePickerField
                                    className="col-md-12 form-group"
                                    name="date"
                                    placeholder="MM/DD/YYYY"
                                    value={values.date}
                                    onChange={() => { }}
                                    disabled={true}
                                    label="Date"
                                  />
                                </Col>
                                <Col md={4}>
                                  <InputField
                                    className="col-md-12 form-group"
                                    type="text"
                                    name="poNumber"
                                    placeholder="Enter Po Number"
                                    value={values.poNumber}
                                    disabled
                                    label="PO #"
                                  />
                                </Col>
                                <Col md={4}>
                                  <InputField
                                    className="col-md-12 form-group"
                                    type="text"
                                    name="vendorName"
                                    placeholder="No vendor"
                                    value={values.vendorName === "N/A" || !values.vendorName ? "No vendor" : values.vendorName}
                                    disabled
                                    label="Vendor"
                                  />
                                </Col>
                              </Row>
                            </Col>

                            <Col md={4}>
                              <div className="">
                                <div>
                                  <Form.Group className="col-md-12 form-group flex flex-end">
                                    <Form.Label
                                      className="d-flex "
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      Ship To
                                      <span
                                        className=" d-flex flex-row"
                                        style={{ width: "100%" }}
                                      >
                                        <Select
                                          closeMenuOnScroll={(e) => {
                                            try {
                                              const target = e.target;

                                              if (
                                                !target ||
                                                !target.nodeType ||
                                                typeof target.closest !==
                                                "function"
                                              ) {
                                                return true;
                                              }

                                              const isMenuScroll =
                                                target.closest(
                                                  '[class*="MenuList"]'
                                                ) ||
                                                target.closest(
                                                  '[class*="menu"]'
                                                );

                                              return !isMenuScroll;
                                            } catch (error) {
                                              return true;
                                            }
                                          }}
                                          options={warehouseOptions}
                                          value={warehouseOptions.find(
                                            (option) =>
                                              option.value ===
                                              values.warehouseId
                                          )}
                                          isClearable
                                          isDisabled
                                          placeholder={
                                            warehouseOptions.length === 0 ? (
                                              <span>No warehouse found</span>
                                            ) : (
                                              "Select Warehouse..."
                                            )
                                          }
                                          noOptionsMessage={() => (
                                            <div
                                              style={{
                                                textAlign: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <span style={{ color: "#666" }}>
                                                No warehouse found.
                                                {(warehouseHasCreatePermission ||
                                                  decodedToken?.roles.some(
                                                    (role) =>
                                                      role.role_type ===
                                                      roleTypes.SUPER_ADMIN
                                                  )) && (
                                                    <span>
                                                      {" "}
                                                      <Link
                                                        to="/dashboard/app/warehouse-add"
                                                        style={{
                                                          color: "#007bff",
                                                          textDecoration: "none",
                                                          cursor: "pointer",
                                                        }}
                                                      >
                                                        Add new
                                                      </Link>
                                                    </span>
                                                  )}
                                              </span>
                                            </div>
                                          )}
                                          menuPortalTarget={document.body} 
                                          menuPosition="fixed" 
                                          menuPlacement="auto"
                                          className="display-content-dropdown"
                                          styles={{
                                            option: (provided, state) => ({
                                              ...provided,

                                              color: "#333",
                                              width: "100%",
                                              backgroundColor: "#fff",
                                            }),
                                            control: (provided) => ({
                                              ...provided,
                                              marginLeft: "1rem",
                                              width: "100%",
                                              color: "#5e667b",
                                              backgroundColor: "#fff",
                                            }),
                                            menuPortal: (provided) => ({
                                              ...provided,
                                              zIndex: 9999,
                                            }),
                                            menu: (provided) => ({
                                              ...provided,
                                              zIndex: 9999,
                                            }),
                                          }}
                                        />
                                      </span>
                                    </Form.Label>

                                    <Form.Control
                                      id="shipTo"
                                      as="textarea"
                                      type="text"
                                      name="shipTo"
                                      placeholder="Enter Shipment Address"
                                      value={values?.shipTo}
                                      disabled
                                      style={{
                                        resize: "none",
                                        height: "150px",
                                        backgroundColor: "#fff",
                                      }}
                                    />
                                  </Form.Group>
                                </div>
                              </div>
                            </Col>
                          </Row>
                          <Row>
                            <Col sm={12} ref={alertRefProduct}>
                              <Card>
                                <Card.Body className="p-0">
                                  <Row>
                                    <Col sm={12} className="m-0 p-0">
                                      <Card.Header className="d-flex justify-content-between">
                                        <div className="header-title d-flex flex-row justify-content-between w-100">
                                          <h5 className="card-title">
                                            Order Products{" "}
                                          </h5>
                                        </div>
                                      </Card.Header>
                                      <div className="d-flex  align-items-center "></div>
                                      <Card.Body className="">
                                        {/* <Box
                                          sx={{
                                            gap: "0rem",
                                            overflow: "auto",
                                            p: "4px",
                                            width: "100%",
                                          }}
                                        >
                                          <MaterialReactTable
                                            table={tableProduct}
                                          />
                                        </Box> */}
                                        <Table
                                          id="order-request-list-table datatable"
                                          className="table table-bordered"
                                          role="grid"
                                          responsive
                                          data-toggle="data-table"
                                          style={{ width: "100%" }}
                                        >
                                          <thead>
                                            <tr
                                              style={{
                                                backgroundColor: "#e2e4e9",
                                              }}
                                            >
                                        
                                              <th style={{ minWidth: "100px" }}>
                                                Quantity
                                              </th>
                                                 <th style={{ minWidth: "200px" }}>
                                                Received Quantity
                                              </th>
                                              <th style={{ minWidth: "200px" }}>
                                                Make
                                              </th>
                                              <th style={{ minWidth: "200px" }}>
                                                Model
                                              </th>
      <th style={{ minWidth: "200px" }}>
                                                Description
                                              </th>
                                              <th style={{ minWidth: "200px" }}>
                                                SKU
                                              </th>
                                                  <th style={{ minWidth: "200px" }}>
                                                Rate
                                              </th>
                                                  <th style={{ minWidth: "200px" }}>
                                                Amount
                                              </th>
<th style={{ minWidth: "200px" }}>
                                                Job
                                              </th>
                                              <th style={{ minWidth: "200px" }}>
                                                System
                                              </th>
                                              <th style={{ minWidth: "200px" }}>
                                                Phase
                                              </th>
                                              <th style={{ minWidth: "200px" }}>
                                                Warehouse
                                              </th>
                                              <th style={{ minWidth: "200px" }}>
                                                Site Name
                                              </th>
                                          
                                            </tr>
                                          </thead>
                                          {values.orderItems &&
                                            values.orderItems.length > 0 ? (
                                            <tbody>
                                              {values.orderItems.map(
                                                (item, index) => (
                                                  <tr key={index}>
                                                  
                                                    <td>
                                                      <Form.Control
                                                        readOnly
                                                        type="number"
                                                        value={item.quantity}
                                                      />
                                                    </td>
                                                           <td>
                                                      <Form.Control
                                                        readOnly
                                                        type="number"
                                                        value={
                                                          item.received_quantity
                                                        }
                                                      />
                                                    </td>
                                                    <td>
                                                      <Form.Control
                                                        readOnly
                                                        type="text"
                                                        value={
                                                          item.manufacturer_name
                                                        }
                                                      />
                                                    </td>

                                                    <td>
                                                      <Form.Control
                                                        readOnly
                                                        title={item.model}
                                                        type="text"
                                                        style={{
                                                          width: "200px",
                                                        }}
                                                        value={item.model || ""}
                                                      />
                                                    </td>
                                                      <td>
                                                      <Form.Control
                                                        title={item.description || ""}
                                                        readOnly
                                                        type="text"
                                                        style={{
                                                          width: "200px",
                                                        }}
                                                        value={item.description || ""}
                                                      />
                                                    </td>
                                                    <td>
                                                      <Form.Control
                                                        title={item.sku}
                                                        readOnly
                                                        type="text"
                                                        style={{
                                                          width: "200px",
                                                        }}
                                                        value={item.sku || ""}
                                                      />
                                                    </td>
  <td> 
                                                      <Form.Control
                                                        readOnly
                                                        type="number"
                                                        style={{
                                                          width: "200px",
                                                        }}
                                                        value={item.rate || ""}
                                                      />
                                                    </td>
                                                     <td>
                                                      <Form.Control
                                                        readOnly
                                                        type="number"
                                                        style={{
                                                          width: "200px",
                                                        }}
                                                        value={item.amount || ""}
                                                      />
                                                    </td>
                                                    <td>
                                                      <div
                                                        title={item?.job_name}
                                                      >
                                                        <Form.Control
                                                          readOnly
                                                          type="text"
                                                          style={{
                                                            width: "200px",
                                                          }}
                                                          value={
                                                            item.job_name || "No Job Assigned"
                                                          }
                                                        />
                                                      </div>
                                                    </td>
                                                    <td>
                                                      <div
                                                        title={
                                                          item?.system_name
                                                        }
                                                      >
                                                        <Form.Control
                                                          readOnly
                                                          type="text"
                                                          style={{
                                                            width: "200px",
                                                          }}
                                                          value={
                                                            item.system_name
                                                          }
                                                        />
                                                      </div>
                                                    </td>

                                                    <td>
                                                      <div
                                                        title={item?.phase_name}
                                                      >
                                                        <Form.Control
                                                          readOnly
                                                          type="text"
                                                          style={{
                                                            width: "200px",
                                                          }}
                                                          value={
                                                            item.phase_name
                                                          }
                                                        />
                                                      </div>
                                                    </td>
                                                    <td>
                                                      <Form.Control
                                                        readOnly
                                                        title={item.warehouse}
                                                        type="text"
                                                        style={{
                                                          width: "200px",
                                                        }}
                                                        value={
                                                          item.warehouse || ""
                                                        }
                                                      />
                                                    </td>

                                             
                                                   

                                                  

                                                    <td>
                                                      <div
                                                        title={item?.site_name}
                                                      >
                                                        <Form.Control
                                                          readOnly
                                                          type="text"
                                                          style={{
                                                            width: "200px",
                                                          }}
                                                          value={item.site_name}
                                                        />
                                                      </div>
                                                    </td>
                                                   
                                                  </tr>
                                                )
                                              )}
                                            </tbody>
                                          ) : (
                                            <tbody>
                                              <tr>
                                                <td colSpan={8}>
                                                  <div className="d-flex w-100 justify-content-center">
                                                    <p>
                                                      No order request item
                                                      found
                                                    </p>
                                                  </div>
                                                </td>
                                              </tr>
                                            </tbody>
                                          )}
                                        </Table>
                                      </Card.Body>
                                    </Col>

                                    <hr />
                                  </Row>
                                </Card.Body>
                              </Card>
                            </Col>
                          </Row>
                        </>
                      ) : (
                        loader && <FadeLoaderComponent />
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default OrderRequestView;
